import { ArrowLeft2, ArrowRight2, TickSquare } from "iconsax-reactjs";
import React, { useRef, useEffect, useState } from "react";
import DateCell from "./DateCell";
import DateRangeSelector from "../DateRangeSelector";

export default function FilterMenu({
    show,
    onClose,
    activeFilter,
    setActiveFilter,
    filters,
    setFilters,
    typeToIcon,
    statusList = ["Failed", "Reversed", "Under review", "Reported", "Pending", "Completed"],
    filterList = ["type", "date", "status"],
}) {
    const ref = useRef();

    // local copy of filters to batch changes
    const [localFilters, setLocalFilters] = useState(filters);

    // sync localFilters when menu opens or parent filters change
    useEffect(() => {
        if (show) {
            setLocalFilters(filters);
            setActiveFilter(null);
        }
    }, [show, filters, setActiveFilter]);

    // close on outside click
    useEffect(() => {
        function handleClick(e) {
            if (show && ref.current && !ref.current.contains(e.target)) {
                onClose();
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [show, onClose]);

    if (!show) return null;

    const applyAndClose = () => {
        setFilters(localFilters);
        setActiveFilter(null);
        onClose();
    };

    const fromSet = Boolean(localFilters.date?.from);
    const toSet = Boolean(localFilters.date?.to);
    const dateCount = [fromSet, toSet].filter(Boolean).length;

    return (
        <div
            ref={ref}
            className={`absolute top-full right-0 mt-2 ${
                activeFilter === "date" ? "md:w-[590px]" : "w-72"
            } bg-white shadow-lg rounded-xl overflow-hidden z-50`}
        >
            {/* MAIN MENU */}
            {!activeFilter && (
                <ul className="divide-y !mb-0 !pl-0 bg-white rounded-lg overflow-hidden">
                    {filterList.map((f) => {
                        const label =
                            f === "status" ? "Status" : f === "date" ? "Date range" : "Type";

                        let hasValue, count;
                        if (f === "status") {
                            hasValue = localFilters.status.length > 0;
                            count = localFilters.status.length;
                        } else if (f === "type") {
                            hasValue = localFilters.type.length > 0;
                            count = localFilters.type.length;
                        } else {
                            hasValue = dateCount > 0;
                            count = dateCount;
                        }

                        return (
                            <li
                                key={f}
                                onClick={() => setActiveFilter(f)}
                                className="flex justify-between items-center px-4 py-3 hover:bg-[#F3F4FB] cursor-pointer"
                            >
                                <span className="text-sm font-medium text-[#101828]">{label}</span>
                                <div className="flex items-center gap-2">
                                    {hasValue && (
                                        <span className="bg-[#6F00FF] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                                            {count}
                                        </span>
                                    )}
                                    <ArrowRight2 size="16" color="#667085" variant="Bold" />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* STATUS SUB‐MENU */}
            {activeFilter === "status" && (
                <div className="w-full overflow-hidden bg-white rounded-b-xl">
                    <div className="flex items-center justify-between px-4 py-3 border-b">
                        <button
                            onClick={() => setActiveFilter(null)}
                            className="focus:outline-none"
                        >
                            <ArrowLeft2 size="20" color="#6F00FF" variant="Outline" />
                        </button>
                        <h3 className="text-lg font-semibold text-[#101828] mb-0">Status</h3>
                        <div className="w-5" />
                    </div>

                    <div className="px-4 space-y-3">
                        {statusList.map((s) => {
                            const checked = localFilters.status.includes(s);
                            return (
                                <div
                                    key={s}
                                    className="flex items-center justify-between cursor-pointer"
                                    onClick={() =>
                                        setLocalFilters((f) => ({
                                            ...f,
                                            status: checked
                                                ? f.status.filter((x) => x !== s)
                                                : [...f.status, s],
                                        }))
                                    }
                                >
                                    <div className="flex items-center gap-3">
                                        {checked ? (
                                            <TickSquare size="20" color="#6F00FF" variant="Bold" />
                                        ) : (
                                            <TickSquare
                                                size="20"
                                                color="#A1A1AA"
                                                variant="Linear"
                                            />
                                        )}
                                        <span className="text-sm text-[#101828]">{s}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="px-4 py-3 border-t">
                        <button
                            onClick={applyAndClose}
                            className="w-full bg-[#6F00FF] text-white py-2 rounded-lg text-sm font-medium"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}

            {/* DATE SUB‐MENU */}
            {activeFilter === "date" && (
                <div className="z-50 w-full bg-white shadow-lg rounded-b-xl">
                    <div className="flex items-center justify-between px-4 py-3 border-b">
                        <button
                            onClick={() => setActiveFilter(null)}
                            className="focus:outline-none"
                        >
                            <ArrowLeft2 size="20" color="#6F00FF" variant="Outline" />
                        </button>
                        <h3 className="text-lg font-semibold mb-0 text-[#101828]">Date range</h3>
                        <div className="w-5" />
                    </div>

                    <div className="px-4">
                        <DateRangeSelector
                            onChange={({ startDate, endDate }) => {
                                setLocalFilters((prev) => ({
                                    ...prev,
                                    date: {
                                        from: startDate,
                                        to: endDate,
                                    },
                                }));
                            }}
                        />
                    </div>

                    <div className="flex justify-end px-4 pb-4 mt-4 border-t">
                        <button
                            onClick={applyAndClose}
                            className="px-6 py-2 bg-[#6F00FF] text-white rounded-lg text-sm font-medium"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}

            {/* TYPE SUB‐MENU */}
            {activeFilter === "type" && (
                <div className="w-full overflow-hidden bg-white rounded-b-xl">
                    <div className="flex items-center justify-between px-4 py-3 border-b">
                        <button
                            onClick={() => setActiveFilter(null)}
                            className="focus:outline-none"
                        >
                            <ArrowLeft2 size="20" color="#6F00FF" variant="Outline" />
                        </button>
                        <h3 className="text-lg font-semibold mb-0 text-[#101828]">Type</h3>
                        <div className="w-5" />
                    </div>

                    <div className="px-4 space-y-3">
                        {typeToIcon
                            ? Object.entries(typeToIcon).map(([key]) => {
                                  const checked = localFilters.type.includes(key);
                                  const label = key.replace(/-/g, " ");
                                  return (
                                      <div
                                          key={key}
                                          className="flex items-center justify-between cursor-pointer"
                                          onClick={() =>
                                              setLocalFilters((f) => ({
                                                  ...f,
                                                  type: checked
                                                      ? f.type.filter((x) => x !== key)
                                                      : [...f.type, key],
                                              }))
                                          }
                                      >
                                          <div className="flex items-center gap-3">
                                              {checked ? (
                                                  <TickSquare
                                                      size="20"
                                                      color="#6F00FF"
                                                      variant="Bold"
                                                  />
                                              ) : (
                                                  <TickSquare
                                                      size="20"
                                                      color="#A1A1AA"
                                                      variant="Linear"
                                                  />
                                              )}
                                              <span className="flex items-center gap-2 text-sm text-[#101828]">
                                                  {label}
                                              </span>
                                          </div>
                                      </div>
                                  );
                              })
                            : null}
                    </div>

                    <div className="px-4 py-3 border-t">
                        <button
                            onClick={applyAndClose}
                            className="w-full bg-[#6F00FF] text-white py-2 rounded-lg text-sm font-medium"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
